import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import BillingModal from './BillingModal';

// --- PREMIUM TOKENS ---
const T = {
  bg: '#ffffff',
  cardBg: '#FFFFFF',
  border: '#E2E8F0',
  textMain: '#0F172A',
  textMuted: '#64748B',
  accent: '#7C3AED',
  accentHover: '#6D28D9',
  green: '#10B981',
  shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05)',
  shadowHover: '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -4px rgba(0, 0, 0, 0.04)',
};

// Actual real-world GPU data matching the Runpod specs
const MOCK_GPUS = [
  { id: 'b300', displayName: 'B300', memoryInGb: 288, systemRam: 503, vcpu: 43, maxInstanceCount: 3, securePrice: 7.39 },
  { id: 'b200', displayName: 'B200', memoryInGb: 180, systemRam: 188, vcpu: 24, maxInstanceCount: 6, securePrice: 5.89 },
  { id: 'h200-nvl', displayName: 'H200 NVL', memoryInGb: 143, systemRam: 250, vcpu: 24, maxInstanceCount: 8, securePrice: 3.79 },
  { id: 'h200-sxm', displayName: 'H200 SXM', memoryInGb: 141, systemRam: 188, vcpu: 12, maxInstanceCount: 8, securePrice: 4.39 },
  { id: 'h100-sxm', displayName: 'H100 SXM', memoryInGb: 80, systemRam: 125, vcpu: 8, maxInstanceCount: 8, securePrice: 2.99 },
  { id: 'h100-nvl', displayName: 'H100 NVL', memoryInGb: 94, systemRam: 173, vcpu: 16, maxInstanceCount: 5, securePrice: 3.19 },
  { id: 'h100-pcie', displayName: 'H100 PCIe', memoryInGb: 80, systemRam: 176, vcpu: 18, maxInstanceCount: 4, securePrice: 2.89 },
  { id: 'mi300x', displayName: 'MI300X', memoryInGb: 192, systemRam: 256, vcpu: 16, maxInstanceCount: 8, securePrice: 2.39 },
  { id: 'rtx-pro-6000', displayName: 'RTX PRO 6000', memoryInGb: 96, systemRam: 188, vcpu: 16, maxInstanceCount: 7, securePrice: 1.99 },
  { id: 'rtx-pro-6000-wk', displayName: 'RTX PRO 6000 WK', memoryInGb: 96, systemRam: 188, vcpu: 16, maxInstanceCount: 4, securePrice: 1.89 },
  { id: 'a100-sxm', displayName: 'A100 SXM', memoryInGb: 80, systemRam: 117, vcpu: 16, maxInstanceCount: 8, securePrice: 1.49 },
  { id: 'a100-pcie', displayName: 'A100 PCIe', memoryInGb: 80, systemRam: 117, vcpu: 16, maxInstanceCount: 4, securePrice: 1.39 },
  { id: 'l40s', displayName: 'L40S', memoryInGb: 48, systemRam: 102, vcpu: 16, maxInstanceCount: 8, securePrice: 0.99 },
  { id: 'rtx-5090', displayName: 'RTX 5090', memoryInGb: 32, systemRam: 60, vcpu: 15, maxInstanceCount: 8, securePrice: 0.99 },
  { id: 'rtx-6000-ada', displayName: 'RTX 6000 Ada', memoryInGb: 48, systemRam: 62, vcpu: 16, maxInstanceCount: 2, securePrice: 0.84 },
  { id: 'l40', displayName: 'L40', memoryInGb: 48, systemRam: 80, vcpu: 12, maxInstanceCount: 8, securePrice: 0.82 },
  { id: 'rtx-pro-4500', displayName: 'RTX PRO 4500', memoryInGb: 32, systemRam: 54, vcpu: 8, maxInstanceCount: 7, securePrice: 0.74 },
  { id: 'rtx-4090', displayName: 'RTX 4090', memoryInGb: 24, systemRam: 31, vcpu: 9, maxInstanceCount: 4, securePrice: 0.69 },
  { id: 'rtx-pro-4000', displayName: 'RTX PRO 4000', memoryInGb: 24, systemRam: 31, vcpu: 12, maxInstanceCount: 4, securePrice: 0.57 },
  { id: 'rtx-a6000', displayName: 'RTX A6000', memoryInGb: 48, systemRam: 50, vcpu: 8, maxInstanceCount: 9, securePrice: 0.53 },
  { id: 'rtx-3090', displayName: 'RTX 3090', memoryInGb: 24, systemRam: 100, vcpu: 6, maxInstanceCount: 2, securePrice: 0.50 },
  { id: 'a40', displayName: 'A40', memoryInGb: 48, systemRam: 50, vcpu: 9, maxInstanceCount: 10, securePrice: 0.44 },
  { id: 'l4', displayName: 'L4', memoryInGb: 24, systemRam: 50, vcpu: 6, maxInstanceCount: 6, securePrice: 0.39 },
  { id: 'rtx-4000-ada', displayName: 'RTX 4000 Ada', memoryInGb: 20, systemRam: 40, vcpu: 8, maxInstanceCount: 8, securePrice: 0.28 },
  { id: 'rtx-a5000', displayName: 'RTX A5000', memoryInGb: 24, systemRam: 48, vcpu: 8, maxInstanceCount: 10, securePrice: 0.27 },
  { id: 'rtx-a4500', displayName: 'RTX A4500', memoryInGb: 20, systemRam: 40, vcpu: 8, maxInstanceCount: 4, securePrice: 0.25 },
  { id: 'rtx-a4000', displayName: 'RTX A4000', memoryInGb: 16, systemRam: 30, vcpu: 8, maxInstanceCount: 8, securePrice: 0.25 },
  { id: 'rtx-2000-ada', displayName: 'RTX 2000 Ada', memoryInGb: 16, systemRam: 31, vcpu: 6, maxInstanceCount: 2, securePrice: 0.24 },
  
  // Restored expanded models with realistic specs & pricing
  { id: 'rtx-5080', displayName: 'RTX 5080', memoryInGb: 16, systemRam: 48, vcpu: 10, maxInstanceCount: 4, securePrice: 0.25 },
  { id: 'rtx-5070-ti', displayName: 'RTX 5070 TI', memoryInGb: 16, systemRam: 48, vcpu: 8, maxInstanceCount: 4, securePrice: 0.18 },
  { id: 'rtx-5070', displayName: 'RTX 5070', memoryInGb: 12, systemRam: 32, vcpu: 8, maxInstanceCount: 4, securePrice: 0.15 },
  { id: 'rtx-5060-ti', displayName: 'RTX 5060 TI', memoryInGb: 16, systemRam: 48, vcpu: 8, maxInstanceCount: 4, securePrice: 0.15 },
  { id: 'rtx-5060', displayName: 'RTX 5060', memoryInGb: 8, systemRam: 16, vcpu: 4, maxInstanceCount: 2, securePrice: 0.10 },
  { id: 'rtx-4080', displayName: 'RTX 4080', memoryInGb: 16, systemRam: 48, vcpu: 10, maxInstanceCount: 8, securePrice: 0.22 },
  { id: 'rtx-4080s', displayName: 'RTX 4080S', memoryInGb: 16, systemRam: 48, vcpu: 10, maxInstanceCount: 8, securePrice: 0.23 },
  { id: 'rtx-4070s-ti', displayName: 'RTX 4070S TI', memoryInGb: 16, systemRam: 48, vcpu: 8, maxInstanceCount: 6, securePrice: 0.19 },
  { id: 'rtx-4070s', displayName: 'RTX 4070S', memoryInGb: 12, systemRam: 32, vcpu: 8, maxInstanceCount: 6, securePrice: 0.15 },
  { id: 'rtx-4060-ti', displayName: 'RTX 4060 TI', memoryInGb: 8, systemRam: 16, vcpu: 4, maxInstanceCount: 4, securePrice: 0.12 },
  { id: 'rtx-4070', displayName: 'RTX 4070', memoryInGb: 12, systemRam: 32, vcpu: 8, maxInstanceCount: 4, securePrice: 0.15 },
  { id: 'rtx-4070-ti', displayName: 'RTX 4070 TI', memoryInGb: 12, systemRam: 32, vcpu: 8, maxInstanceCount: 4, securePrice: 0.18 },
  { id: 'rtx-4060', displayName: 'RTX 4060', memoryInGb: 8, systemRam: 16, vcpu: 4, maxInstanceCount: 4, securePrice: 0.10 },
  { id: 'a100-sxm4', displayName: 'A100 SXM4', memoryInGb: 80, systemRam: 125, vcpu: 16, maxInstanceCount: 8, securePrice: 1.49 },
  { id: 'a10', displayName: 'A10', memoryInGb: 24, systemRam: 64, vcpu: 8, maxInstanceCount: 4, securePrice: 0.35 },
  { id: 'rtx-3090-ti', displayName: 'RTX 3090 TI', memoryInGb: 24, systemRam: 64, vcpu: 12, maxInstanceCount: 2, securePrice: 0.55 },
  { id: 'rtx-3080-ti', displayName: 'RTX 3080 TI', memoryInGb: 12, systemRam: 32, vcpu: 8, maxInstanceCount: 2, securePrice: 0.30 },
  { id: 'rtx-3080', displayName: 'RTX 3080', memoryInGb: 10, systemRam: 20, vcpu: 6, maxInstanceCount: 2, securePrice: 0.25 },
  { id: 'rtx-3070', displayName: 'RTX 3070', memoryInGb: 8, systemRam: 16, vcpu: 4, maxInstanceCount: 2, securePrice: 0.18 },
  { id: 'rtx-3060-laptop', displayName: 'RTX 3060 LAPTOP', memoryInGb: 6, systemRam: 12, vcpu: 4, maxInstanceCount: 1, securePrice: 0.10 },
  { id: 'rtx-3060', displayName: 'RTX 3060', memoryInGb: 12, systemRam: 32, vcpu: 8, maxInstanceCount: 4, securePrice: 0.15 },
  { id: 'rtx-3060-ti', displayName: 'RTX 3060 TI', memoryInGb: 8, systemRam: 16, vcpu: 4, maxInstanceCount: 2, securePrice: 0.16 },
  { id: 'q-rtx-8000', displayName: 'Q RTX 8000', memoryInGb: 48, systemRam: 96, vcpu: 12, maxInstanceCount: 4, securePrice: 0.65 },
  { id: 'rtx-2060s', displayName: 'RTX 2060S', memoryInGb: 8, systemRam: 16, vcpu: 4, maxInstanceCount: 2, securePrice: 0.12 },
  { id: 'rtx-2080-ti', displayName: 'RTX 2080 TI', memoryInGb: 11, systemRam: 32, vcpu: 6, maxInstanceCount: 2, securePrice: 0.18 },
  { id: 'rtx-2060', displayName: 'RTX 2060', memoryInGb: 6, systemRam: 12, vcpu: 4, maxInstanceCount: 2, securePrice: 0.10 },
  { id: 'tesla-v100', displayName: 'TESLA V100', memoryInGb: 32, systemRam: 64, vcpu: 8, maxInstanceCount: 4, securePrice: 0.45 },
  { id: 'gtx-1080-ti', displayName: 'GTX 1080 TI', memoryInGb: 11, systemRam: 32, vcpu: 6, maxInstanceCount: 2, securePrice: 0.12 },
  { id: 'gtx-1070-ti', displayName: 'GTX 1070 TI', memoryInGb: 8, systemRam: 16, vcpu: 4, maxInstanceCount: 2, securePrice: 0.08 },
  { id: 'gtx-1080', displayName: 'GTX 1080', memoryInGb: 8, systemRam: 16, vcpu: 4, maxInstanceCount: 2, securePrice: 0.10 },
  { id: 'quadro-p4000', displayName: 'QUADRO P4000', memoryInGb: 8, systemRam: 16, vcpu: 4, maxInstanceCount: 1, securePrice: 0.15 },
  { id: 'titan-xp', displayName: 'TITAN XP', memoryInGb: 12, systemRam: 32, vcpu: 6, maxInstanceCount: 1, securePrice: 0.18 },
  { id: 'rtx-pro-5000', displayName: 'RTX PRO 5000', memoryInGb: 48, systemRam: 96, vcpu: 12, maxInstanceCount: 4, securePrice: 0.75 },
  { id: 'rtx-5880-ada', displayName: 'RTX 5880 Ada', memoryInGb: 48, systemRam: 167, vcpu: 10, maxInstanceCount: 2, securePrice: 0.79 },
  { id: 'gtx-1660-s', displayName: 'GTX 1660 S', memoryInGb: 6, systemRam: 12, vcpu: 4, maxInstanceCount: 1, securePrice: 0.08 }
];

const TEMPLATES = [
  // Official PyTorch
  { id: 't1', name: 'DAGGPT PyTorch 2.4.0', type: 'Official', os: 'Ubuntu 22.04', env: 'PyTorch, CUDA 12.1' },
  { id: 't2', name: 'DAGGPT PyTorch 2.3.0', type: 'Official', os: 'Ubuntu 22.04', env: 'PyTorch, CUDA 12.1' },
  { id: 't3', name: 'DAGGPT PyTorch 2.2.1', type: 'Official', os: 'Ubuntu 22.04', env: 'PyTorch, CUDA 12.1' },
  { id: 't4', name: 'DAGGPT PyTorch 2.1.0', type: 'Official', os: 'Ubuntu 22.04', env: 'PyTorch, CUDA 11.8' },
  { id: 't5', name: 'DAGGPT PyTorch 2.0.1', type: 'Official', os: 'Ubuntu 22.04', env: 'PyTorch, CUDA 11.8' },
  { id: 't6', name: 'DAGGPT PyTorch 1.13.1', type: 'Official', os: 'Ubuntu 20.04', env: 'PyTorch, CUDA 11.7' },
  
  // Official TensorFlow
  { id: 't7', name: 'DAGGPT TensorFlow 2.14.0', type: 'Official', os: 'Ubuntu 22.04', env: 'TensorFlow, CUDA 11.8' },
  { id: 't8', name: 'DAGGPT TensorFlow 2.13.0', type: 'Official', os: 'Ubuntu 22.04', env: 'TensorFlow, CUDA 11.8' },
  { id: 't9', name: 'DAGGPT TensorFlow 2.12.0', type: 'Official', os: 'Ubuntu 22.04', env: 'TensorFlow, CUDA 11.8' },
  { id: 't10', name: 'DAGGPT TensorFlow 2.11.0', type: 'Official', os: 'Ubuntu 20.04', env: 'TensorFlow, CUDA 11.2' },

  // Base OS
  { id: 't11', name: 'DAGGPT Base', type: 'Official', os: 'Ubuntu 22.04', env: 'Base OS, CUDA 12.1' },
  { id: 't12', name: 'DAGGPT Base (CUDA 11.8)', type: 'Official', os: 'Ubuntu 22.04', env: 'Base OS, CUDA 11.8' },
  { id: 't13', name: 'DAGGPT Base (CUDA 11.7)', type: 'Official', os: 'Ubuntu 20.04', env: 'Base OS, CUDA 11.7' },

  // Specialized AI Servers (Official)
  { id: 't14', name: 'vLLM OpenAI Compatible Server', type: 'Official', os: 'Ubuntu 22.04', env: 'vLLM, Python 3.10' },
  { id: 't15', name: 'Fast Stable Diffusion', type: 'Official', os: 'Ubuntu 22.04', env: 'A1111, Python 3.10' },
  { id: 't16', name: 'Ollama', type: 'Official', os: 'Ubuntu 22.04', env: 'Ollama, CUDA' },
  { id: 't17', name: 'Kohya_ss', type: 'Official', os: 'Ubuntu 22.04', env: 'Kohya, Python 3.10' },
  { id: 't18', name: 'Text Generation WebUI', type: 'Official', os: 'Ubuntu 22.04', env: 'Oobabooga, Python 3.10' },
  { id: 't19', name: 'Jupyter Notebook', type: 'Official', os: 'Ubuntu 22.04', env: 'Jupyter, PyTorch' },
  { id: 't20', name: 'ComfyUI', type: 'Official', os: 'Ubuntu 22.04', env: 'ComfyUI, PyTorch' },
  { id: 't21', name: 'Axolotl', type: 'Official', os: 'Ubuntu 22.04', env: 'Axolotl, PyTorch' },
  { id: 't22', name: 'TensorRT-LLM', type: 'Official', os: 'Ubuntu 22.04', env: 'TensorRT, CUDA 12.1' },
  { id: 't23', name: 'Stable Video Diffusion', type: 'Official', os: 'Ubuntu 22.04', env: 'SVD, PyTorch' },
  { id: 't24', name: 'WhisperX', type: 'Official', os: 'Ubuntu 22.04', env: 'WhisperX, PyTorch' },
  
  // Community Templates
  { id: 't25', name: 'EveryDream2Trainer', type: 'Community', os: 'Ubuntu 22.04', env: 'ED2, Python 3.10' },
  { id: 't26', name: 'Fooocus', type: 'Community', os: 'Ubuntu 22.04', env: 'Fooocus, Python 3.10' },
  { id: 't27', name: 'AutoGPT', type: 'Community', os: 'Ubuntu 22.04', env: 'AutoGPT, Python 3.10' },
  { id: 't28', name: 'Tabby - Self-hosted GitHub Copilot', type: 'Community', os: 'Ubuntu 22.04', env: 'Tabby, CUDA 11.8' },
  { id: 't29', name: 'LocalAI', type: 'Community', os: 'Ubuntu 22.04', env: 'LocalAI, CUDA 12.1' },
  { id: 't30', name: 'Llama.cpp Server', type: 'Community', os: 'Ubuntu 22.04', env: 'Llama.cpp, CUDA 12.1' },
  { id: 't31', name: 'Mistral-v0.2 Server', type: 'Community', os: 'Ubuntu 22.04', env: 'Mistral, vLLM' },
  { id: 't32', name: 'Mixtral 8x7B API', type: 'Community', os: 'Ubuntu 22.04', env: 'Mixtral, vLLM' },
  { id: 't33', name: 'Dolphin-Mixtral', type: 'Community', os: 'Ubuntu 22.04', env: 'Dolphin, Ollama' },
  { id: 't34', name: 'Stable Diffusion XL (SDXL)', type: 'Community', os: 'Ubuntu 22.04', env: 'SDXL, ComfyUI' },
  { id: 't35', name: 'Midjourney Clone (OpenJourney)', type: 'Community', os: 'Ubuntu 22.04', env: 'OpenJourney, A1111' },
  { id: 't36', name: 'AudioLDM 2', type: 'Community', os: 'Ubuntu 22.04', env: 'AudioLDM, PyTorch' },
  { id: 't37', name: 'Bark Text-to-Audio', type: 'Community', os: 'Ubuntu 22.04', env: 'Bark, PyTorch' },
  { id: 't38', name: 'MusicGen (Audiocraft)', type: 'Community', os: 'Ubuntu 22.04', env: 'MusicGen, PyTorch' },
  { id: 't39', name: 'Llama 3 8B Instruct', type: 'Community', os: 'Ubuntu 22.04', env: 'Llama 3, vLLM' },
  { id: 't40', name: 'Llama 3 70B Instruct', type: 'Community', os: 'Ubuntu 22.04', env: 'Llama 3, vLLM' },
  { id: 't41', name: 'Phi-3 Mini', type: 'Community', os: 'Ubuntu 22.04', env: 'Phi-3, vLLM' },
  { id: 't42', name: 'Gemma 2 9B', type: 'Community', os: 'Ubuntu 22.04', env: 'Gemma 2, vLLM' },
  { id: 't43', name: 'Gemma 2 27B', type: 'Community', os: 'Ubuntu 22.04', env: 'Gemma 2, vLLM' },
  { id: 't44', name: 'Qwen 2 72B', type: 'Community', os: 'Ubuntu 22.04', env: 'Qwen 2, vLLM' },
  { id: 't45', name: 'Qwen 2.5 7B', type: 'Community', os: 'Ubuntu 22.04', env: 'Qwen 2.5, vLLM' },
  { id: 't46', name: 'DeepSeek Coder V2', type: 'Community', os: 'Ubuntu 22.04', env: 'DeepSeek, vLLM' },
  { id: 't47', name: 'CodeLlama 34B', type: 'Community', os: 'Ubuntu 22.04', env: 'CodeLlama, vLLM' },
  { id: 't48', name: 'StarCoder 2', type: 'Community', os: 'Ubuntu 22.04', env: 'StarCoder, vLLM' },
  { id: 't49', name: 'Stable LM 2 12B', type: 'Community', os: 'Ubuntu 22.04', env: 'Stable LM, vLLM' },
  { id: 't50', name: 'Zephyr 7B Beta', type: 'Community', os: 'Ubuntu 22.04', env: 'Zephyr, vLLM' },
  { id: 't51', name: 'Yi-34B Chat', type: 'Community', os: 'Ubuntu 22.04', env: 'Yi, vLLM' },
  { id: 't52', name: 'Nous Hermes 2', type: 'Community', os: 'Ubuntu 22.04', env: 'Nous Hermes, vLLM' },
  { id: 't53', name: 'OpenHermes 2.5', type: 'Community', os: 'Ubuntu 22.04', env: 'OpenHermes, vLLM' },
  { id: 't54', name: 'NeuralBeagle14-7B', type: 'Community', os: 'Ubuntu 22.04', env: 'NeuralBeagle, vLLM' },
  { id: 't55', name: 'Krita AI Diffusion Server', type: 'Community', os: 'Ubuntu 22.04', env: 'Krita AI, ComfyUI' },
  { id: 't56', name: 'InvokeAI', type: 'Community', os: 'Ubuntu 22.04', env: 'InvokeAI, Python 3.10' },
  { id: 't57', name: 'DreamBooth Trainer', type: 'Community', os: 'Ubuntu 22.04', env: 'DreamBooth, PyTorch' },
  { id: 't58', name: 'SADTalker', type: 'Community', os: 'Ubuntu 22.04', env: 'SADTalker, PyTorch' },
  { id: 't59', name: 'Wav2Lip', type: 'Community', os: 'Ubuntu 22.04', env: 'Wav2Lip, PyTorch' },
  { id: 't60', name: 'DeepFaceLive', type: 'Community', os: 'Ubuntu 22.04', env: 'DeepFaceLive, PyTorch' },
  { id: 't61', name: 'Roop - Deepfake', type: 'Community', os: 'Ubuntu 22.04', env: 'Roop, PyTorch' },
  { id: 't62', name: 'FaceFusion', type: 'Community', os: 'Ubuntu 22.04', env: 'FaceFusion, PyTorch' },
  { id: 't63', name: 'AnimateDiff', type: 'Community', os: 'Ubuntu 22.04', env: 'AnimateDiff, ComfyUI' },
  { id: 't64', name: 'Deforum Stable Diffusion', type: 'Community', os: 'Ubuntu 22.04', env: 'Deforum, Python 3.10' },
  { id: 't65', name: 'DragGAN', type: 'Community', os: 'Ubuntu 22.04', env: 'DragGAN, PyTorch' },
  { id: 't66', name: 'XTTS v2 (Coqui)', type: 'Community', os: 'Ubuntu 22.04', env: 'XTTS, PyTorch' },
  { id: 't67', name: 'RVC (Retrieval-based Voice Conversion)', type: 'Community', os: 'Ubuntu 22.04', env: 'RVC, PyTorch' },
  { id: 't68', name: 'So-VITS-SVC', type: 'Community', os: 'Ubuntu 22.04', env: 'So-VITS, PyTorch' },
  { id: 't69', name: 'Auto-GPTQ', type: 'Community', os: 'Ubuntu 22.04', env: 'Auto-GPTQ, PyTorch' },
  { id: 't70', name: 'ExLlamaV2', type: 'Community', os: 'Ubuntu 22.04', env: 'ExLlamaV2, PyTorch' }
];

const getArchitecture = (name) => {
  if (!name) return 'NVIDIA';
  const n = name.toUpperCase();
  if (n.includes('B300') || n.includes('B200') || n.includes('5090') || n.includes('5080') || n.includes('5070') || n.includes('5060') || n.includes('PRO 6000 S') || n.includes('PRO 6000 WS')) return 'BLACKWELL';
  if (n.includes('H200') || n.includes('H100')) return 'HOPPER';
  if (n.includes('MI300X') || n.includes('MI250') || n.includes('MI210')) return 'CDNA 3';
  if (n.includes('L40') || n.includes('L4') || n.includes('4090') || n.includes('4080') || n.includes('4070') || n.includes('4060') || n.includes('6000 ADA') || n.includes('5880 ADA')) return 'ADA LOVELACE';
  if (n.includes('A100') || n.includes('A6000') || n.includes('A40') || n.includes('A10') || n.includes('A5000') || n.includes('A4000') || n.includes('A2000') || n.includes('3090') || n.includes('3080') || n.includes('3070') || n.includes('3060')) return 'AMPERE';
  if (n.includes('V100')) return 'VOLTA';
  if (n.includes('8000') || n.includes('2080') || n.includes('2060')) return 'TURING';
  if (n.includes('1080') || n.includes('1070') || n.includes('P4000') || n.includes('TITAN')) return 'PASCAL';
  return 'NVIDIA';
};

export default function SentinelNodes() {
  const [gpus, setGpus] = useState([]);
  const [templates, setTemplates] = useState(TEMPLATES);
  const [loading, setLoading] = useState(true);
  const [selectedGpu, setSelectedGpu] = useState(null);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('All GPUs');
  const [computeMode, setComputeMode] = useState('GPU'); // GPU or CPU
  
  const [gpuCount, setGpuCount] = useState(1);
  const [pricingMode, setPricingMode] = useState('On-Demand');
  const [containerDisk, setContainerDisk] = useState(40);
  const [persistentDisk, setPersistentDisk] = useState(20);

  const [selectedTemplate, setSelectedTemplate] = useState(TEMPLATES[0]);
  const [showSpecsModal, setShowSpecsModal] = useState(null); // holds gpu object

  // Template Modal States
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateTab, setTemplateTab] = useState('My templates');

  // Billing Modal
  const [showBillingModal, setShowBillingModal] = useState(false);

  // Deploy state
  const [deployStatus, setDeployStatus] = useState('idle');

  // Fetch actual RunPod GPUs
  useEffect(() => {
    const fetchGpus = async () => {
      try {
        const API_URL = process.env.NODE_ENV === 'development' ? 'http://127.0.0.1:8787' : 'https://api.daggpt.network';
        const res = await fetch(`${API_URL}/api/compute/gpus`);
        const data = await res.json();
        
        if (data && data.data && data.data.gpuTypes) {
          setGpus(data.data.gpuTypes);
          setSelectedGpu(data.data.gpuTypes[0]);
        } else {
          console.error("Invalid API response format", data);
          setGpus(MOCK_GPUS);
          setSelectedGpu(MOCK_GPUS[0]);
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setGpus(MOCK_GPUS);
        setSelectedGpu(MOCK_GPUS[0]);
      } finally {
        setLoading(false);
      }
    };

    const fetchTemplates = async () => {
      try {
        const API_URL = process.env.NODE_ENV === 'development' ? 'http://127.0.0.1:8787' : 'https://api.daggpt.network';
        const res = await fetch(`${API_URL}/api/compute/templates`);
        const data = await res.json();
        if (data?.data?.myself?.podTemplates) {
          const rawTemplates = data.data.myself.podTemplates;
          const formatted = rawTemplates.map((t) => ({
            id: t.id,
            name: t.name.replace(/Runpod/gi, 'DAGGPT'),
            type: 'Global',
            os: t.imageName.split(':')[0] || 'Docker',
            env: t.imageName.split(':')[1] || t.imageName
          }));
          setTemplates([...TEMPLATES, ...formatted]);
        }
      } catch (err) {
        console.error("Fetch templates error:", err);
      }
    };

    fetchGpus();
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (computeMode === 'CPU') setActiveTab('All CPUs');
    else setActiveTab('All GPUs');
  }, [computeMode]);

  const getPrice = (gpu) => {
    return gpu.securePrice || gpu.secureCloud || gpu.lowestPrice?.uninterruptablePrice || 0;
  };

  const getSystemRam = (gpu) => gpu.systemRam || Math.ceil(gpu.memoryInGb * 1.5); // Use explicit if available
  const getVcpu = (gpu) => gpu.vcpu || (gpu.memoryInGb >= 80 ? 16 : 8); // Use explicit if available

  const gpuCost = selectedGpu ? getPrice(selectedGpu) * gpuCount : 0;
  const cDiskCost = containerDisk * 0.003;
  const pDiskCost = persistentDisk * 0.002;
  const totalCost = gpuCost + cDiskCost + pDiskCost;

  const handleDeploy = async () => {
    setDeployStatus('loading');
    try {
      const API_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:8787' : 'https://api.daggpt.network';
      const res = await fetch(`${API_URL}/api/compute/rent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          gpuTypeId: selectedGpu.id, 
          gpuCount,
          templateId: selectedTemplate.id
        })
      });
      
      const data = await res.json();
      
      if (res.status === 402 || data.error?.includes('Insufficient balance')) {
        setDeployStatus('idle');
        setShowBillingModal(true);
        return;
      }
      
      if (data.error) {
        alert(data.error);
        setDeployStatus('error');
        setTimeout(() => setDeployStatus('idle'), 3000);
        return;
      }
      
      setDeployStatus('success');
      alert('Deployed successfully! Head to the "My Pods" tab on the left to manage it.');
      setTimeout(() => setDeployStatus('idle'), 3000);
    } catch (err) {
      setDeployStatus('error');
      setTimeout(() => setDeployStatus('idle'), 3000);
    }
  };
  const MOCK_CPUS = [
    { id: 'cpu-2', displayName: '2 vCPU', memoryInGb: 4, securePrice: 0.015, maxGpuCount: 100 },
    { id: 'cpu-4', displayName: '4 vCPU', memoryInGb: 8, securePrice: 0.03, maxGpuCount: 100 },
    { id: 'cpu-8', displayName: '8 vCPU', memoryInGb: 16, securePrice: 0.06, maxGpuCount: 100 },
    { id: 'cpu-16', displayName: '16 vCPU', memoryInGb: 32, securePrice: 0.12, maxGpuCount: 100 },
    { id: 'cpu-32', displayName: '32 vCPU', memoryInGb: 64, securePrice: 0.24, maxGpuCount: 100 },
    { id: 'cpu-64', displayName: '64 vCPU', memoryInGb: 128, securePrice: 0.48, maxGpuCount: 100 },
    { id: 'cpu-128', displayName: '128 vCPU', memoryInGb: 256, securePrice: 0.96, maxGpuCount: 100 },
    { id: 'cpu-256', displayName: '256 vCPU', memoryInGb: 512, securePrice: 1.92, maxGpuCount: 100 },
  ];

  const sourceData = computeMode === 'CPU' ? MOCK_CPUS : gpus;
  const currentTabs = computeMode === 'CPU' 
    ? ['All CPUs', 'High Memory', 'Cost Efficient']
    : ['All GPUs', 'Recommended', 'High Performance', 'Cost Efficient', 'Latest'];

  const filteredData = sourceData.filter(g => {
    if (g.id === 'unknown') return false;
    if (!g.displayName.toLowerCase().includes(search.toLowerCase())) return false;
    
    if (computeMode === 'GPU') {
      if (activeTab === 'Recommended') return g.memoryInGb >= 24 && getPrice(g) < 1.0;
      if (activeTab === 'High Performance') return g.memoryInGb >= 80;
      if (activeTab === 'Cost Efficient') return getPrice(g) < 0.5;
      if (activeTab === 'Latest') return ['Ada', 'Blackwell', 'Hopper', 'H100', 'H200', '4090', '5090'].some(t => g.displayName.includes(t));
    } else {
      if (activeTab === 'High Memory') return g.memoryInGb >= 64;
      if (activeTab === 'Cost Efficient') return getPrice(g) < 0.1;
    }
    return true; 
  });

  return (
    <div style={{ background: '#ffffff', minHeight: '100%', padding: '24px 32px', fontFamily: '"Inter", sans-serif', position: 'relative' }}>
      
      {/* PAGE HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 800, color: T.textMain, margin: '0 0 6px', letterSpacing: '-0.02em' }}>
            Deploy GPU Instance
          </h1>
          <p style={{ margin: 0, color: T.textMuted, fontSize: '14px', fontWeight: 500 }}>
            Select the perfect GPU for your AI workload
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
        
        {/* ========================================== */}
        {/* LEFT COLUMN: MAIN CONTENT                  */}
        {/* ========================================== */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          {/* 1. DARK PURPLE BANNER */}
          <div style={{ 
            backgroundImage: 'url(/banner.png)', 
            backgroundSize: 'cover',
            backgroundPosition: 'center right',
            backgroundRepeat: 'no-repeat',
            minHeight: '380px',
            borderRadius: '20px', 
            padding: '32px 40px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            boxShadow: '0 20px 40px rgba(124, 58, 237, 0.15)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize: '30px 30px', pointerEvents: 'none' }} />
            
            <div style={{ position: 'relative', zIndex: 1 }}>
              <h2 style={{ fontSize: '28px', fontWeight: 700, color: '#fff', margin: '0 0 12px', letterSpacing: '-0.02em' }}>
                Powerful GPUs. Instant Deployments.
              </h2>
              <p style={{ margin: '0 0 24px', color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
                Built for AI training, inference, and everything in between.
              </p>
              <div style={{ display: 'flex', gap: '16px' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#fff', fontSize: '13px', fontWeight: 600, background: 'rgba(255,255,255,0.1)', padding: '6px 12px', borderRadius: '12px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.accent} strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                  Instant Deploy
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#fff', fontSize: '13px', fontWeight: 600, background: 'rgba(255,255,255,0.1)', padding: '6px 12px', borderRadius: '12px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                  High Performance
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#fff', fontSize: '13px', fontWeight: 600, background: 'rgba(255,255,255,0.1)', padding: '6px 12px', borderRadius: '12px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                  Global Infrastructure
                </span>
              </div>
            </div>
          </div>

          {/* 2. CONTROLS BAR */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', background: '#fff', padding: '4px', borderRadius: '16px', border: `1px solid ${T.border}`, boxShadow: T.shadow }}>
              <button 
                onClick={() => setComputeMode('GPU')}
                style={{
                  background: computeMode === 'GPU' ? T.accent : 'transparent',
                  color: computeMode === 'GPU' ? '#fff' : T.textMuted,
                  border: 'none', padding: '10px 24px', borderRadius: '12px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
                  display: 'flex', alignItems: 'center', gap: '8px'
                }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/><line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="2" y1="7" x2="7" y2="7"/><line x1="2" y1="17" x2="7" y2="17"/><line x1="17" y1="17" x2="22" y2="17"/><line x1="17" y1="7" x2="22" y2="7"/></svg>
                GPU
              </button>
              <button 
                onClick={() => setComputeMode('CPU')}
                style={{
                  background: computeMode === 'CPU' ? '#F1F5F9' : 'transparent',
                  color: computeMode === 'CPU' ? T.textMain : T.textMuted,
                  border: 'none', padding: '10px 24px', borderRadius: '12px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
                  display: 'flex', alignItems: 'center', gap: '8px'
                }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="4" width="16" height="16" rx="2" ry="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/><line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/><line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/></svg>
                CPU
              </button>
            </div>

            <div style={{ flex: 1, display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <div style={{ position: 'relative', width: '260px' }}>
                <input 
                  type="text" placeholder="Search GPUs..." value={search} onChange={(e) => setSearch(e.target.value)}
                  style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', border: `1px solid ${T.border}`, background: '#fff', fontSize: '13px', color: T.textMain, outline: 'none', boxShadow: T.shadow }} 
                />
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="2" style={{ position: 'absolute', right: '16px', top: '14px' }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </div>
              <button style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 16px', borderRadius: '12px', border: `1px solid ${T.border}`, background: '#fff', fontSize: '13px', fontWeight: 600, color: T.textMain, cursor: 'pointer', boxShadow: T.shadow }}>
                Filters <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
            </div>
          </div>

          {/* 3. TABS */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${T.border}`, paddingBottom: '16px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              {currentTabs.map(tab => (
                <button 
                  key={tab} onClick={() => setActiveTab(tab)}
                  style={{
                    background: activeTab === tab ? T.accent : 'transparent',
                    color: activeTab === tab ? '#fff' : T.textMuted,
                    border: 'none', padding: '8px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s'
                  }}>
                  {tab}
                </button>
              ))}
            </div>
            <button style={{ background: 'transparent', border: 'none', color: T.textMain, fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
              Compare (0)
            </button>
          </div>

          {/* 4. GPU GRID */}
          {loading ? (
             <div style={{ padding: '40px', textAlign: 'center', color: T.textMuted }}>Loading Global Inventory...</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
              {filteredData.map((gpu) => {
                const isSelected = selectedGpu?.id === gpu.id;
                
                return (
                  <motion.div
                    key={gpu.id}
                    onClick={() => setSelectedGpu(gpu)}
                    whileHover={{ y: -2, boxShadow: T.shadowHover }}
                    style={{
                      width: '100%',
                      background: isSelected ? '#F5F3FF' : '#FFFFFF',
                      borderRadius: '12px',
                      border: `1px solid ${isSelected ? T.accent : T.border}`,
                      boxShadow: isSelected ? `0 0 0 1px ${T.accent}, 0 2px 4px rgba(0,0,0,0.02)` : '0 2px 4px rgba(0,0,0,0.02)',
                      padding: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px',
                      transition: 'all 0.2s',
                    }}
                  >
                    {/* Top Row: Name and Price */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ fontSize: '16px', fontWeight: 800, color: isSelected ? T.accentHover : T.textMain, letterSpacing: '-0.01em' }}>
                        {gpu.displayName}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '15px', fontWeight: 800, color: T.textMain }}>
                          ${getPrice(gpu).toFixed(2)}<span style={{ fontSize: '12px', fontWeight: 600, color: T.textMuted }}>/hr</span>
                        </div>
                      </div>
                    </div>

                    {/* Middle Row: VRAM and Max Instances */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: T.textMuted }}>
                        {computeMode === 'GPU' ? gpu.memoryInGb : (gpu.memoryInGb * 4)}GB {computeMode === 'GPU' ? 'VRAM' : 'RAM'}
                      </div>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: T.textMuted }}>
                        {gpu.maxInstanceCount || 8} max
                      </div>
                    </div>

                    {/* Bottom Row: System Specs and Availability */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: '16px', borderTop: `1px solid ${isSelected ? 'rgba(124, 58, 237, 0.1)' : '#F1F5F9'}` }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: T.textMuted }}>
                        {computeMode === 'GPU' ? getSystemRam(gpu) : gpu.memoryInGb}GB RAM • {getVcpu(gpu)} vCPU
                      </div>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {/* 3 Availability Pills */}
                        <div style={{ width: '12px', height: '6px', borderRadius: '4px', background: T.accent }} />
                        <div style={{ width: '12px', height: '6px', borderRadius: '4px', background: T.accent }} />
                        <div style={{ width: '12px', height: '6px', borderRadius: '4px', background: '#E2E8F0' }} />
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* 5. WORKLOAD (TEMPLATE) SELECTOR */}
          <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', border: `1px solid ${T.border}`, marginTop: '16px' }}>
             <div style={{ fontSize: '15px', fontWeight: 800, color: T.textMain, marginBottom: '16px' }}>Workload</div>
             
             <div 
               onClick={() => setShowTemplateModal(true)}
               style={{
                 display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                 padding: '16px', borderRadius: '12px', border: `1px solid ${T.border}`,
                 background: '#fff', cursor: 'pointer', transition: 'all 0.2s',
                 boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
               }}
               onMouseEnter={(e) => { e.currentTarget.style.border = `1px solid ${T.accent}`; e.currentTarget.style.background = '#F5F3FF'; }}
               onMouseLeave={(e) => { e.currentTarget.style.border = `1px solid ${T.border}`; e.currentTarget.style.background = '#fff'; }}
             >
               <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                 <div style={{ width: 44, height: 44, borderRadius: '10px', background: 'linear-gradient(135deg, #7C3AED 0%, #3B82F6 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 4px 10px rgba(124,58,237,0.2)' }}>
                   <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>
                 </div>
                 <div>
                   <div style={{ fontSize: '14px', fontWeight: 800, color: T.textMain }}>{selectedTemplate?.name || 'Select a template'} <span style={{fontSize:'11px', padding:'2px 6px', background:'#F1F5F9', borderRadius:'4px', color:T.textMuted, marginLeft:'4px'}}>{selectedTemplate?.type}</span></div>
                   <div style={{ fontSize: '13px', color: T.textMuted, marginTop: '2px' }}>{selectedTemplate?.type === 'Official' ? selectedTemplate.os : selectedTemplate?.env || selectedTemplate?.os || selectedTemplate?.imageName}</div>
                 </div>
               </div>
               <div style={{ fontSize: '13px', fontWeight: 700, color: T.textMain, display: 'flex', alignItems: 'center', gap: '4px', background: '#F8FAFC', padding: '8px 12px', borderRadius: '8px', border: `1px solid ${T.border}` }}>
                 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                 Change
               </div>
             </div>
          </div>

          {/* 6. BOTTOM SECTION (Config + Storage) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '24px' }}>
            
            {/* Left: GPU Count & Pricing Mode */}
            <div>
              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: T.textMain, marginBottom: '12px' }}>GPU Count</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[1,2,3,4,5,6,7,8].map(num => (
                    <button 
                      key={num} onClick={() => setGpuCount(num)} disabled={!selectedGpu || num > (selectedGpu.maxInstanceCount || 8)}
                      style={{
                        flex: 1, padding: '10px 0', borderRadius: '8px',
                        background: gpuCount === num ? T.accent : '#fff',
                        color: gpuCount === num ? '#fff' : (!selectedGpu || num > (selectedGpu.maxInstanceCount || 8) ? '#CBD5E1' : T.textMain),
                        border: `1px solid ${gpuCount === num ? T.accent : T.border}`,
                        fontSize: '13px', fontWeight: 600, cursor: (!selectedGpu || num > (selectedGpu.maxInstanceCount || 8)) ? 'not-allowed' : 'pointer'
                      }}>
                      {num}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '16px' }}>
                <div onClick={() => setPricingMode('On-Demand')} style={{ flex: 1, padding: '16px', background: pricingMode === 'On-Demand' ? '#F5F3FF' : '#fff', borderRadius: '12px', border: `2px solid ${pricingMode === 'On-Demand' ? T.accent : T.border}`, cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 700, color: T.textMain, marginBottom: '4px' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.accent} strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                    On-Demand
                  </div>
                  <div style={{ fontSize: '12px', color: T.textMuted, marginBottom: '12px', lineHeight: 1.4 }}>Pay as you go, with costs based on actual usage time.</div>
                  <div style={{ fontSize: '16px', fontWeight: 800, color: T.textMain }}>${selectedGpu ? getPrice(selectedGpu).toFixed(2) : '0.00'} <span style={{fontSize:'12px', color:T.textMuted, fontWeight:500}}>/hr</span></div>
                </div>
                <div onClick={() => setPricingMode('Reserved')} style={{ flex: 1, padding: '16px', background: '#fff', borderRadius: '12px', border: `1px solid ${T.border}`, cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 700, color: T.textMain, marginBottom: '4px' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    Reserved
                  </div>
                  <div style={{ fontSize: '12px', color: T.textMuted, marginBottom: '12px', lineHeight: 1.4 }}>Lock in a lower price for long-term usage.</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: '14px', fontWeight: 800, color: T.textMain }}>12+ months</div>
                    <span style={{ fontSize: '12px', color: T.accent, fontWeight: 600 }}>Talk to Sales</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Storage Config */}
            <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', border: `1px solid ${T.border}` }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: T.textMain, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                Storage Configuration <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: T.textMain }}>Container Disk</div>
                  <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '2px', maxWidth: '160px' }}>Temporary storage that will be erased when the Pod is stopped.</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button onClick={() => setContainerDisk(Math.max(10, containerDisk - 10))} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${T.border}`, background: '#F8FAFC', cursor: 'pointer', fontWeight: 600 }}>-</button>
                  <div style={{ width: 48, textAlign: 'center', fontSize: '13px', fontWeight: 700 }}>{containerDisk}</div>
                  <div style={{ fontSize: '13px', color: T.textMuted, fontWeight: 600 }}>GB</div>
                  <button onClick={() => setContainerDisk(containerDisk + 10)} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${T.border}`, background: '#F8FAFC', cursor: 'pointer', fontWeight: 600 }}>+</button>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: T.textMain }}>Persistent Storage</div>
                  <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '2px', maxWidth: '160px' }}>Independent from Pod's lifecycle.</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button onClick={() => setPersistentDisk(Math.max(10, persistentDisk - 10))} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${T.border}`, background: '#F8FAFC', cursor: 'pointer', fontWeight: 600 }}>-</button>
                  <div style={{ width: 48, textAlign: 'center', fontSize: '13px', fontWeight: 700 }}>{persistentDisk}</div>
                  <div style={{ fontSize: '13px', color: T.textMuted, fontWeight: 600 }}>GB</div>
                  <button onClick={() => setPersistentDisk(persistentDisk + 10)} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${T.border}`, background: '#F8FAFC', cursor: 'pointer', fontWeight: 600 }}>+</button>
                </div>
              </div>
              
              <div style={{ height: 1, background: T.border, margin: '16px 0' }} />
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: T.textMain, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="9" y1="3" x2="9" y2="21"/></svg>
                  Automatically Create Network Volume
                </div>
                <div style={{ width: 36, height: 20, background: T.accent, borderRadius: 10, position: 'relative', cursor: 'pointer' }}>
                  <div style={{ position: 'absolute', top: 2, right: 2, width: 16, height: 16, background: '#fff', borderRadius: '50%' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ========================================== */}
        {/* RIGHT COLUMN: SIDEBAR                      */}
        {/* ========================================== */}
        <div style={{ width: '360px', display: 'flex', flexDirection: 'column', gap: '24px', position: 'sticky', top: '24px', alignSelf: 'flex-start' }}>
          
          {/* SUMMARY BOX */}
          <div style={{ background: '#fff', borderRadius: '24px', boxShadow: T.shadow, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
            
            {/* Top Light Blue banner */}
            <div style={{ background: 'linear-gradient(135deg, #F0FDF4 0%, #E0F2FE 100%)', padding: '24px', position: 'relative' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 800, color: T.textMain }}>Deployment Summary</h3>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <div style={{ width: 48, height: 48, background: '#fff', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.accent} strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
                </div>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 800, color: T.textMain }}>{selectedTemplate.name}</div>
                  <div style={{ fontSize: '13px', color: T.textMuted, fontWeight: 600, marginTop: '2px' }}>{gpuCount} x {selectedGpu?.displayName || 'Select a GPU'}</div>
                  {selectedGpu && (
                    <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '2px' }}>{selectedGpu.memoryInGb}GB VRAM • {getSystemRam(selectedGpu)}GB RAM • {getVcpu(selectedGpu)} vCPU</div>
                  )}
                </div>
              </div>
            </div>

            {/* Price & Breakdown */}
            <div style={{ padding: '24px' }}>
              <div style={{ fontSize: '12px', color: T.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total / Hour</div>
              <div style={{ fontSize: '32px', fontWeight: 800, color: T.textMain, margin: '4px 0 8px', letterSpacing: '-0.03em' }}>
                ${totalCost.toFixed(3)} <span style={{fontSize: '14px', color: T.textMuted, fontWeight: 600}}>USD</span>
              </div>
              <div style={{ fontSize: '12px', color: T.textMuted, display: 'flex', alignItems: 'center', gap: '4px' }}>
                Billed per millisecond <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
              </div>

              <div style={{ height: 1, background: T.border, margin: '20px 0' }} />

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', cursor: 'pointer' }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color: T.textMain }}>Breakdown</div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="2"><polyline points="18 15 12 9 6 15"/></svg>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: T.textMuted }}>Total disk</span>
                  <span style={{ fontWeight: 600, color: T.textMain }}>{containerDisk + persistentDisk} GB</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: T.textMuted }}>GPU cost</span>
                  <span style={{ fontWeight: 600, color: T.textMain }}>${gpuCost.toFixed(3)}/hour</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: T.textMuted }}>Container disk cost</span>
                  <span style={{ fontWeight: 600, color: T.textMain }}>${cDiskCost.toFixed(3)}/hour</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: T.textMuted }}>Network volume cost</span>
                  <span style={{ fontWeight: 600, color: T.textMain }}>${pDiskCost.toFixed(3)}/hour</span>
                </div>
              </div>

              {/* Alert Box */}
              <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: '12px', padding: '16px', marginTop: '24px', display: 'flex', gap: '12px' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" style={{flexShrink: 0}}><rect x="4" y="4" width="16" height="16" rx="2" ry="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/><line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/><line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/></svg>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#065F46', marginBottom: '4px' }}>Network volume will be created</div>
                  <div style={{ fontSize: '11px', color: '#047857', lineHeight: 1.4 }}>A network volume will be created and attached to this Pod on deployment.</div>
                </div>
              </div>

              {/* Deploy Button */}
              <button 
                onClick={handleDeploy}
                disabled={deployStatus === 'loading' || !selectedGpu}
                style={{
                  width: '100%', padding: '16px', marginTop: '24px', borderRadius: '12px', border: 'none',
                  background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
                  color: '#fff', fontSize: '15px', fontWeight: 700, cursor: 'pointer',
                  boxShadow: '0 10px 25px -5px rgba(124, 58, 237, 0.4)',
                  display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px',
                  transition: 'transform 0.2s',
                  opacity: deployStatus === 'loading' || !selectedGpu ? 0.7 : 1
                }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {deployStatus === 'loading' ? 'Deploying...' : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg>
                    Deploy Pod
                  </>
                )}
              </button>
            </div>
          </div>

          {/* TOP UP BALANCE */}
          <div style={{ background: '#fff', borderRadius: '24px', padding: '24px', boxShadow: T.shadow, border: `1px solid ${T.border}` }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: 800, color: T.textMain }}>Account Balance</h3>
            <div style={{ fontSize: '12px', color: T.textMuted, marginBottom: '20px', lineHeight: 1.5 }}>
              Top up your balance using Fiat, Crypto, or DGCC to rent Sentinel Nodes.
            </div>
            
            <button 
              onClick={() => setShowBillingModal(true)}
              style={{
                width: '100%', padding: '14px', borderRadius: '12px', background: '#F8FAFC', color: T.accent, border: `1px solid ${T.accent}`, fontSize: '14px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all 0.2s'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = T.accent; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#F8FAFC'; e.currentTarget.style.color = T.accent; }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
              Top Up Balance
            </button>
          </div>

          {/* HELPFUL LINKS */}
          <div style={{ background: '#fff', borderRadius: '24px', padding: '24px', boxShadow: T.shadow, border: `1px solid ${T.border}` }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '14px', fontWeight: 800, color: T.textMain }}>Helpful Links</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', color: T.textMuted }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', fontWeight: 600 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg> Pricing
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', color: T.textMuted }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', fontWeight: 600 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg> Documentation
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* GPU MODAL OVERLAY */}
      <AnimatePresence>
        {showSpecsModal && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(8px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setShowSpecsModal(null)}
          >
            <motion.div 
              initial={{ y: 50, scale: 0.95 }} animate={{ y: 0, scale: 1 }} exit={{ y: 20, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              style={{ width: '100%', maxWidth: '600px', background: '#fff', borderRadius: '24px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', overflow: 'hidden' }}
            >
              {/* Modal Header */}
              <div style={{ background: 'linear-gradient(135deg, #1A103C 0%, #2D1A54 100%)', padding: '32px', color: '#fff' }}>
                 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h2 style={{ margin: '0 0 8px', fontSize: '24px', fontWeight: 800 }}>{showSpecsModal.displayName}</h2>
                      <div style={{ display: 'flex', gap: '12px', fontSize: '13px', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                         <span>NVIDIA Ampere / Hopper</span>
                         <span>•</span>
                         <span>PCIe / SXM Interface</span>
                      </div>
                    </div>
                    <button onClick={() => setShowSpecsModal(null)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                 </div>
              </div>

              {/* Modal Body */}
              <div style={{ padding: '32px' }}>
                <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 800, color: T.textMain }}>Hardware Specifications</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '32px' }}>
                   <div style={{ background: '#F8FAFC', padding: '16px', borderRadius: '12px', border: `1px solid ${T.border}` }}>
                      <div style={{ fontSize: '12px', color: T.textMuted, fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>GPU Memory (VRAM)</div>
                      <div style={{ fontSize: '18px', fontWeight: 800, color: T.textMain }}>{showSpecsModal.memoryInGb} GB <span style={{fontSize:'12px', color:T.textMuted, fontWeight:600}}>GDDR6X / HBM3</span></div>
                   </div>
                   <div style={{ background: '#F8FAFC', padding: '16px', borderRadius: '12px', border: `1px solid ${T.border}` }}>
                      <div style={{ fontSize: '12px', color: T.textMuted, fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>System Memory (RAM)</div>
                      <div style={{ fontSize: '18px', fontWeight: 800, color: T.textMain }}>~{getSystemRam(showSpecsModal)} GB</div>
                   </div>
                   <div style={{ background: '#F8FAFC', padding: '16px', borderRadius: '12px', border: `1px solid ${T.border}` }}>
                      <div style={{ fontSize: '12px', color: T.textMuted, fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>Compute Cores (vCPU)</div>
                      <div style={{ fontSize: '18px', fontWeight: 800, color: T.textMain }}>{getVcpu(showSpecsModal)} Cores</div>
                   </div>
                   <div style={{ background: '#F8FAFC', padding: '16px', borderRadius: '12px', border: `1px solid ${T.border}` }}>
                      <div style={{ fontSize: '12px', color: T.textMuted, fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>Base Hourly Rate</div>
                      <div style={{ fontSize: '18px', fontWeight: 800, color: T.textMain }}>${getPrice(showSpecsModal).toFixed(3)} <span style={{fontSize:'12px', color:T.textMuted, fontWeight:600}}>/ hr</span></div>
                   </div>
                </div>

                <div style={{ display: 'flex', gap: '16px' }}>
                   <button 
                     onClick={() => { setSelectedGpu(showSpecsModal); setShowSpecsModal(null); }}
                     style={{ flex: 1, background: T.accent, color: '#fff', border: 'none', padding: '16px', borderRadius: '12px', fontSize: '15px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px rgba(124,58,237,0.3)' }}>
                     Select for Deployment
                   </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* TEMPLATE SELECTION MODAL */}
      <AnimatePresence>
        {showTemplateModal && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}
            onClick={() => setShowTemplateModal(false)}
          >
            <motion.div 
              initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.98 }} transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={e => e.stopPropagation()}
              style={{ background: '#fff', width: '100%', maxWidth: '1100px', height: '85vh', borderRadius: '24px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              {/* Modal Header */}
              <div style={{ padding: '24px 32px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: '24px' }}>
                <div style={{ fontSize: '18px', fontWeight: 800, color: T.textMain, whiteSpace: 'nowrap' }}>Select a template</div>
                
                <div style={{ flex: 1, position: 'relative', maxWidth: '400px', margin: '0 auto' }}>
                  <svg style={{ position: 'absolute', left: 16, top: 12, color: T.textMuted }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <input 
                    type="text" 
                    placeholder="Search templates" 
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                    style={{ width: '100%', padding: '10px 16px 10px 42px', borderRadius: '8px', border: `1px solid ${T.border}`, fontSize: '14px', outline: 'none' }}
                  />
                </div>
                
                <div onClick={() => setShowTemplateModal(false)} style={{ cursor: 'pointer', color: T.textMuted }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </div>
              </div>

              {/* Tabs */}
              <div style={{ display: 'flex', gap: '32px', padding: '0 32px', borderBottom: `1px solid ${T.border}`, background: '#F8FAFC' }}>
                {['My templates', 'Public', 'All', 'Official', 'Verified', 'Community'].map(tab => (
                  <div 
                    key={tab} 
                    onClick={() => setTemplateTab(tab)}
                    style={{ 
                      padding: '16px 0', fontSize: '14px', fontWeight: templateTab === tab ? 700 : 500, 
                      color: templateTab === tab ? T.textMain : T.textMuted, cursor: 'pointer',
                      borderBottom: templateTab === tab ? `2px solid ${T.accent}` : '2px solid transparent'
                    }}>
                    {tab}
                  </div>
                ))}
              </div>

              {/* Grid */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '32px', background: '#F8FAFC' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                  {templates
                    .filter(t => {
                       // Filter by tab
                       if (templateTab === 'My templates') return true; 
                       if (templateTab === 'Official') return t.type === 'Official';
                       if (templateTab === 'Community') return t.type === 'Community';
                       if (templateTab === 'Public' || templateTab === 'All' || templateTab === 'Verified') return true;
                       return true;
                    })
                    .filter(t => (t.name + (t.env||'')).toLowerCase().includes(templateSearch.toLowerCase()))
                    .map(template => (
                    <div 
                      key={template.id}
                      onClick={() => { setSelectedTemplate(template); setShowTemplateModal(false); }}
                      style={{
                        background: '#fff', borderRadius: '12px', padding: '20px', border: `1px solid ${T.border}`,
                        display: 'flex', flexDirection: 'column', cursor: 'pointer', transition: 'all 0.2s',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.border = `1px solid ${T.accent}`; e.currentTarget.style.boxShadow = T.shadowHover; }}
                      onMouseLeave={(e) => { e.currentTarget.style.border = `1px solid ${T.border}`; e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.02)'; }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                        <div style={{ width: 32, height: 32, borderRadius: '8px', background: template.type === 'Community' ? '#F1F5F9' : '#F5F3FF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: template.type === 'Community' ? T.textMuted : T.accent }}>
                           {template.type === 'Community' ? (
                             <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 2 22 22 22"/></svg>
                           ) : (
                             <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>
                           )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <div style={{ width: 28, height: 28, borderRadius: '6px', background: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg></div>
                          <div style={{ width: 28, height: 28, borderRadius: '6px', background: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></div>
                        </div>
                      </div>
                      
                      <div style={{ fontSize: '14px', fontWeight: 800, color: T.textMain, marginBottom: '8px' }}>{template.name}</div>
                      <div style={{ fontSize: '12px', color: T.textMuted, marginBottom: 'auto' }}>{template.env || template.os || template.imageName}</div>
                      
                      <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: T.textMuted }}>
                         <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={template.type === 'Community' ? T.textMuted : T.accent} strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                         {template.type}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BillingModal isOpen={showBillingModal} onClose={() => setShowBillingModal(false)} />
    </div>
  );
}
